import React, { useState, useEffect, useCallback } from 'react';
import {
  FaLeaf,
  FaChartLine,
  FaHistory,
  FaDownload,
  FaFilter,
  FaMapMarkerAlt,
  FaWater,
  FaSeedling,
  FaExchangeAlt,
  FaTimes,
  FaInfoCircle,
  FaSpinner
} from 'react-icons/fa';
import apiService from '../dashboard/services/apiService';
import { sensorService } from '../services/SensorService';
import { yieldPredictor } from '../services/YieldPredictor';
import FarmMap from './FarmMap';

const CropEvaluation = () => {
  const [lands, setLands] = useState([
    {
      id: 1,
      name: 'North Pivot',
      location: 'Upper Marlboro, MD',
      size: '120 acres',
      currentCrop: 'Corn',
      soilType: 'Sandy Loam',
      lastRotation: 'March 2023',
      healthIndex: 92,
      sustainabilityScore: 88,
      yieldPrediction: '185 bu/acre',
      recommendations: [
        'Nitrogen levels optimal - no action needed',
        'Monitor soil moisture for potential weekend rain'
      ],
      history: [180, 175, 182, 168] // Past 4 years
    },
    {
      id: 2,
      name: 'River Bottom',
      location: 'Upper Marlboro, MD',
      size: '45 acres',
      currentCrop: 'Soybeans',
      soilType: 'Silty Loam',
      lastRotation: 'May 2023',
      healthIndex: 68,
      sustainabilityScore: 72,
      yieldPrediction: '52 bu/acre',
      recommendations: [
        'High moisture detected - risk of fungal growth',
        'Deploy scout drone for visual inspection'
      ],
      history: [48, 50, 45, 52]
    },
    {
      id: 3,
      name: 'Home Sector',
      location: 'Upper Marlboro, MD',
      size: '15 acres',
      currentCrop: 'Wheat',
      soilType: 'Clay Loam',
      lastRotation: 'Oct 2023',
      healthIndex: 85,
      sustainabilityScore: 90,
      yieldPrediction: '78 bu/acre',
      recommendations: [
        'Ready for cover crop planning',
        'Soil compaction within acceptable limits'
      ],
      history: [75, 78, 72, 80]
    }
  ]);

  const [selectedLand, setSelectedLand] = useState(null);

  // New state for comparison feature
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [isComparing, setIsComparing] = useState(false);

  // API integration state
  const [loadingPredictions, setLoadingPredictions] = useState({});
  const [apiError, setApiError] = useState(null);

  // Phase 4: AI State
  const [liveYield, setLiveYield] = useState(null);

  useEffect(() => {
    // Subscribe to the digital twin simulation
    const unsubscribe = sensorService.subscribe((data) => {
      // 1. Update Sensor Readings (Raw Data)
      if (selectedLand) {
        // Run AI Logic (The Brain)
        const sensorObj = {
          moisture: data.moisture,
          ph: data.ph,
          nitrogen: data.nitrogen,
          temperature: data.temperature
        };

        // Run Oracle (Yield Prediction) with History
        const prediction = yieldPredictor.predictYield(
          selectedLand.currentCrop,
          sensorObj,
          selectedLand.history
        );
        setLiveYield(prediction);
      }
    }, selectedLand?.id || 1);
    return () => unsubscribe();
  }, [selectedLand]); // Re-subscribe when land changes to ensure context is correct

  useEffect(() => {
    // Auto-select the first land on initial load if not comparing
    if (lands.length > 0 && !selectedLand && !isComparing) {
      setSelectedLand(lands[0]);
    }
  }, [lands, selectedLand, isComparing]);

  // Fetch yield prediction from API
  const fetchYieldPrediction = useCallback(async (land) => {
    if (loadingPredictions[land.id]) return; // Already loading

    setLoadingPredictions(prev => ({ ...prev, [land.id]: true }));
    setApiError(null);

    try {
      // Prepare features based on land data
      // Map land properties to ML model features
      const features = {
        temp_avg: 25.5, // Default - in production, get from weather API
        precipitation: 50.2, // Default - in production, get from weather API
        gdd: 1500, // Growing Degree Days - default
        crop_diversity_index: 3, // Based on rotation history
        soil_health: land.healthIndex,
        water_availability: land.sustainabilityScore,
      };

      // Adjust based on crop type
      if (land.currentCrop === 'Corn') {
        features.gdd = 2000;
      } else if (land.currentCrop === 'Wheat') {
        features.gdd = 1200;
      } else if (land.currentCrop === 'Rice') {
        features.precipitation = 80.0;
      }

      const response = await apiService.getPrediction(features);

      // Update the land with real prediction
      const predictionValue = response.prediction.toFixed(1);
      const predictionText = `${predictionValue} tons/acre`;

      setLands(prevLands =>
        prevLands.map(l =>
          l.id === land.id
            ? { ...l, yieldPrediction: predictionText, predictionModel: response.model_name }
            : l
        )
      );
    } catch (error) {
      console.error('Error fetching yield prediction:', error);
      setApiError(`Failed to fetch prediction: ${error.message}`);
      // Keep the existing hardcoded value if API fails
    } finally {
      setLoadingPredictions(prev => {
        const newState = { ...prev };
        delete newState[land.id];
        return newState;
      });
    }
  }, [loadingPredictions]);

  // Fetch prediction when a land is selected
  useEffect(() => {
    if (selectedLand && !selectedLand.predictionModel) {
      // Only fetch if we don't already have a prediction from API
      fetchYieldPrediction(selectedLand);
    }
  }, [selectedLand, fetchYieldPrediction]);

  const getHealthColor = (score) => {
    if (score >= 80) return 'var(--accent-green)';
    if (score >= 60) return 'var(--accent-yellow)';
    return 'var(--accent-red)';
  };

  const handleSelectLand = (land) => {
    setSelectedLand(land);
  };



  const getTypeIcon = (type) => {
    switch (type) {
      case 'Wheat':
        return <FaSeedling />;
      case 'Corn':
        return <FaLeaf />;
      case 'Rice':
        return <FaWater />;
      case 'Soybeans':
        return <FaSeedling />;
      default:
        return <FaLeaf />;
    }
  };

  const exportReport = () => {
    if (!selectedLand) return;

    // In a real application, this would generate a PDF or CSV
    alert(`Exporting report for ${selectedLand.name}...`);
  };





  const startComparison = () => {
    if (selectedForComparison.length < 2) {
      alert('Please select at least 2 lands to compare.');
      return;
    }

    setIsComparing(true);
  };

  const resetComparison = () => {
    setIsComparing(false);
    setSelectedForComparison([]);

    // Auto-select the first land when exiting comparison mode
    if (lands.length > 0) {
      setSelectedLand(lands[0]);
    }
  };

  // Helper function to render comparison metrics
  const renderComparisonMetrics = () => {
    // If no lands are selected for comparison, show a message
    if (selectedForComparison.length === 0) {
      return (
        <div className="no-comparison-data">
          <FaInfoCircle className="empty-icon" />
          <p>No lands selected for comparison</p>
        </div>
      );
    }

    // If only one land is selected, prompt to select more
    if (selectedForComparison.length === 1) {
      return (
        <div className="no-comparison-data">
          <FaInfoCircle className="empty-icon" />
          <p>Please select at least one more land to compare</p>
        </div>
      );
    }

    return (
      <>
        {/* Basic Metrics Comparison */}
        <div className="comparison-section">
          <h4>Basic Information</h4>
          <div className="comparison-metrics-grid">
            <div className="comparison-metric-row">
              <div className="metric-label">Current Crop</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">
                  <div className="metric-icon-small">
                    {getTypeIcon(land.currentCrop)}
                  </div>
                  {land.currentCrop}
                </div>
              ))}
            </div>

            <div className="comparison-metric-row">
              <div className="metric-label">Soil Type</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">{land.soilType}</div>
              ))}
            </div>

            <div className="comparison-metric-row">
              <div className="metric-label">Last Rotation</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">{land.lastRotation}</div>
              ))}
            </div>

            <div className="comparison-metric-row">
              <div className="metric-label">
                Yield Prediction
                {selectedForComparison.some(l => loadingPredictions[l.id]) && (
                  <FaSpinner className="spinning" style={{ marginLeft: '8px', fontSize: '12px' }} />
                )}
              </div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">
                  {loadingPredictions[land.id] ? 'Loading...' : land.yieldPrediction}
                  {land.predictionModel && (
                    <span className="model-badge-small" title={`Using ${land.predictionModel} model`}>
                      ML
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Health Index Comparison */}
        <div className="comparison-section">
          <h4>Health Index Comparison</h4>
          <div className="health-comparison">
            {selectedForComparison.map(land => (
              <div key={land.id} className="health-comparison-item">
                <div className="health-comparison-label">{land.name}</div>
                <div className="health-comparison-bar-container">
                  <div
                    className="health-comparison-bar"
                    style={{
                      width: `${land.healthIndex}%`,
                      backgroundColor: getHealthColor(land.healthIndex)
                    }}
                  ></div>
                </div>
                <div className="health-comparison-value" style={{ color: getHealthColor(land.healthIndex) }}>
                  {land.healthIndex}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sustainability Factors Comparison */}
        <div className="comparison-section">
          <h4>Sustainability Factors</h4>

          <div className="sustainability-comparison">
            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Biodiversity</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 75 : land.id === 2 ? 60 : land.id === 3 ? 55 : 65;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div
                          className="factor-comparison-bar"
                          style={{
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Water Usage</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 85 : land.id === 2 ? 70 : land.id === 3 ? 50 : 65;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div
                          className="factor-comparison-bar"
                          style={{
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Soil Health</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 90 : land.id === 2 ? 65 : land.id === 3 ? 60 : 75;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div
                          className="factor-comparison-bar"
                          style={{
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Risk Assessment Comparison */}
        <div className="comparison-section">
          <h4>Risk Assessment</h4>

          <div className="risk-comparison">
            <div className="risk-factor-group">
              <div className="factor-comparison-label">Pest Pressure</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 30 : land.id === 2 ? 60 : land.id === 3 ? 85 : 45;
                  const label = land.id === 1 ? 'Low' : land.id === 2 ? 'Medium' : land.id === 3 ? 'High' : 'Medium';
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div
                          className="factor-comparison-bar"
                          style={{
                            width: `${value}%`,
                            backgroundColor: getHealthColor(land.id === 1 ? 70 : land.id === 2 ? 40 : land.id === 3 ? 15 : 55)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="risk-factor-group">
              <div className="factor-comparison-label">Weather Vulnerability</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 50 : land.id === 2 ? 75 : land.id === 3 ? 55 : 40;
                  const label = land.id === 1 ? 'Medium' : land.id === 2 ? 'High' : land.id === 3 ? 'Medium' : 'Low';
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div
                          className="factor-comparison-bar"
                          style={{
                            width: `${value}%`,
                            backgroundColor: getHealthColor(land.id === 1 ? 50 : land.id === 2 ? 25 : land.id === 3 ? 45 : 60)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations Comparison */}
        <div className="comparison-section">
          <h4>AI Recommendations</h4>
          <div className="recommendations-comparison">
            {selectedForComparison.map(land => (
              <div key={land.id} className="land-recommendations">
                <h5>{land.name} Recommendations</h5>
                <ul className="recommendations-list">
                  {land.recommendations.map((rec, index) => (
                    <li key={index} className="recommendation-item">{rec}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="crop-evaluation">
      <div className="section-header">
        <h2>Crop Evaluation</h2>
        {selectedForComparison.length > 0 && (
          <div className="comparison-controls">
            <span className="selected-count">
              {selectedForComparison.length} land{selectedForComparison.length !== 1 ? 's' : ''} selected
            </span>
            <button
              className="compare-button"
              onClick={startComparison}
              disabled={selectedForComparison.length < 2}
            >
              <FaExchangeAlt /> Compare Lands
            </button>
          </div>
        )}
      </div>

      {isComparing ? (
        // Comparison View
        <div className="comparison-container">
          <div className="comparison-header">
            <h3>Land Comparison</h3>
            <button className="reset-button" onClick={resetComparison}>
              <FaTimes /> Exit Comparison
            </button>
          </div>

          <div className="comparison-content">
            {renderComparisonMetrics()}
          </div>
        </div>
      ) : (
        // Regular View
        <div className="flex flex-col gap-6">
          {/* Map & Spatial Command Section */}
          <div className="w-full">
            <FarmMap
              onFieldSelect={handleSelectLand}
              selectedFieldId={selectedLand?.id}
              fieldsData={lands}
            />
          </div>

          {/* Selected Field Details (HUD) - Only shows when a field is selected */}
          {selectedLand ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="evaluation-header">
                <div className="header-content">
                  <h3>{selectedLand.name}</h3>
                  <div className="header-meta">
                    <span className="header-location">
                      <FaMapMarkerAlt style={{ marginRight: '4px' }} /> {selectedLand.location}
                    </span>
                    <span className="header-size">{selectedLand.size}</span>
                  </div>
                </div>
                <div className="header-actions">
                  <button className="download-button" onClick={exportReport}>
                    <FaDownload /> Export Report
                  </button>
                </div>
              </div>

              <div className="evaluation-metrics">
                <div className="metric-card">
                  <div className="metric-icon">
                    <FaLeaf />
                  </div>
                  <div className="metric-details">
                    <div className="metric-label">Current Crop</div>
                    <div className="metric-value">{selectedLand.currentCrop}</div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">
                    <FaChartLine />
                  </div>
                  <div className="metric-details">
                    <div className="metric-label">
                      Yield Prediction
                      {selectedLand.predictionModel && (
                        <span className="model-badge" title={`Using ${selectedLand.predictionModel} model`}>
                          ML
                        </span>
                      )}
                      {loadingPredictions[selectedLand.id] && (
                        <FaSpinner className="spinning" style={{ marginLeft: '8px', fontSize: '12px' }} />
                      )}
                    </div>
                    <div className="metric-value">
                      {liveYield ? `${liveYield.value} ${liveYield.unit}` : (loadingPredictions[selectedLand.id] ? 'Loading...' : selectedLand.yieldPrediction)}
                    </div>
                    {apiError && (
                      <div className="api-error" style={{ fontSize: '11px', color: '#f44336', marginTop: '4px' }}>
                        {apiError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">
                    <FaHistory />
                  </div>
                  <div className="metric-details">
                    <div className="metric-label">Last Rotation</div>
                    <div className="metric-value">{selectedLand.lastRotation}</div>
                  </div>
                </div>

                {/* Yield Analysis Breakdown */}
                <div className="metric-card col-span-2 md:col-span-1 bg-surface-card border border-border-strong rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FaChartLine className="text-secondary" />
                    <span className="font-semibold text-sm uppercase text-text-secondary">Yield Analysis</span>
                  </div>
                  <div className="space-y-2">
                    {liveYield && liveYield.factors && liveYield.factors.map((factor, i) => (
                      <div key={i} className="flex justify-between items-center text-xs">
                        <span className="text-text-muted">{factor.name}</span>
                        <span className={`font-mono font-bold ${factor.impact === 'positive' ? 'text-green-500' : (factor.impact === 'negative' ? 'text-red-500' : 'text-text-primary')}`}>
                          {factor.value}
                        </span>
                      </div>
                    ))}
                    {!liveYield && <p className="text-xs text-muted-foreground">Gathering data...</p>}
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">
                    <FaFilter />
                  </div>
                  <div className="metric-details">
                    <div className="metric-label">Soil Type</div>
                    <div className="metric-value">{selectedLand.soilType}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass p-8 text-center rounded-3xl border border-dashed border-gray-500/30">
              <FaMapMarkerAlt className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-xl font-medium text-muted-foreground">Select a Field from the Map</h3>
              <p className="text-sm text-muted-foreground mt-2">Click on any colored polygon to view live diagnostics.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CropEvaluation; 